import { createFileRoute } from "@tanstack/react-router";
import { SiteShell } from "@/components/site/SiteShell";
import { PillarArticle, articleJsonLdForPillar } from "@/components/site/PillarArticle";
import { buildHead, breadcrumbJsonLd, faqJsonLd } from "@/lib/seo";
import cover from "@/assets/cover-pillar-performance.jpg";
import cover640 from "@/assets/cover-pillar-performance-640.webp";
import cover960 from "@/assets/cover-pillar-performance-960.webp";
import cover1280 from "@/assets/cover-pillar-performance-1280.webp";
import { setEdgeHtmlCache } from "@/lib/cache-headers";

const COVER_SRCSET = `${cover640} 640w, ${cover960} 960w, ${cover1280} 1280w`;
const COVER_SIZES = "(min-width: 768px) 720px, calc(100vw - 48px)";
const PATH = "/resources/sitewide-lcp-regression-36-urls";
const ALT = "/es/recursos/regresion-lcp-36-urls";
const TITLE = "How We Diagnosed a Site-Wide LCP Regression Across 36 URLs · Shugert";
const DESC =
  "A field note on diagnosing a shared desktop LCP regression: Search Console grouping, cache coverage, third-party contention, LCP candidates and honest validation.";
const PUBLISHED = "2026-07-28T20:00:00.000Z";
const MODIFIED = "2026-07-28T20:00:00.000Z";

const SECTIONS = [
  {
    id: "what-search-console-reported",
    heading: "The signal: 36 desktop URLs failed as one group",
    blocks: [
      "Google Search Console grouped 36 desktop URLs under the same poor Core Web Vitals issue: Largest Contentful Paint above four seconds. The affected set included several commercial routes, not one isolated landing page.",
      "That grouping changed the investigation. When many URLs share the same failure pattern, the first question is not which page has the largest image. It is which rendering, delivery or third-party behavior those routes have in common.",
      "### What the report did not prove",
      "Search Console did not identify one root cause, and it did not prove that every affected visit had the same LCP element. Field reports aggregate real users over a rolling window. They are strong evidence of a population-level problem, not a line-level debugger.",
    ],
  },
  {
    id: "turn-the-group-into-a-test-plan",
    heading: "Turn a URL group into a small test matrix",
    blocks: [
      "Testing all 36 URLs independently would create a large report without necessarily increasing certainty. We selected representative route families and states that exercised the shared layout, asset loading and server response paths.",
      "- Homepage and one high-value service page.",
      "- A commercial page using the shared service-page layout.",
      "- A resource hub and a long-form resource.",
      "- English and Spanish equivalents where the same template was reused.",
      "- Cold and warm cache requests.",
      "The goal was not to find the fastest page. It was to reproduce the common delay and separate server response, resource discovery, rendering and third-party work.",
    ],
  },
  {
    id: "shared-causes",
    heading: "The shared causes we investigated first",
    blocks: [
      "### Cache coverage",
      "The edge-cache rules covered the homepage, resources and a limited set of generic routes, but several commercial route families were outside the public cache path. Those pages repeated server rendering and data work instead of benefiting from the same shared delivery policy.",
      "### Work competing inside the LCP window",
      "Accessibility tooling, analytics and session-replay code were useful, but they did not need to start while the first meaningful content was still settling. A third party can be asynchronous and still create main-thread or network contention at the wrong moment.",
      "### LCP candidates hidden until hydration",
      "Animation wrappers that begin at opacity zero can delay when a headline or hero is considered painted. The likely LCP text and image should be visible in the server-rendered HTML instead of waiting for client hydration and an IntersectionObserver.",
      "### Measurement without element context",
      "A route-level score is more useful when it also records the element that produced LCP and the interaction associated with INP. Without that context, a regression alert says that something changed but makes the next investigation slower.",
    ],
  },
  {
    id: "changes-we-shipped",
    heading: "The bounded changes we shipped",
    blocks: [
      "- Expanded shared public edge caching to the commercial route families while keeping admin, portal, checkout, API and user-specific pages private.",
      "- Added CDN cache headers intended for the Cloudflare delivery path used by the application.",
      "- Deferred non-critical accessibility and session-replay tooling until after load and idle time.",
      "- Loaded Google Analytics only after analytics consent, then scheduled it outside the initial render path.",
      "- Kept the primary headline and hero image outside opacity-before-hydration animation wrappers.",
      "- Added route, rating, element and connection context to the existing LCP, INP and CLS events.",
      "Each change corresponded to a named failure mode. We did not combine the performance work with a redesign, copy rewrite or broad dependency upgrade, because that would make the before-and-after harder to interpret.",
    ],
  },
  {
    id: "why-we-did-not-call-it-fixed",
    heading: "Why deployment is not the same as a resolved field issue",
    blocks: [
      "The changes are deployed, but Search Console field data does not refresh like a Lighthouse run. Its Core Web Vitals reporting uses a rolling population window, so older slow visits remain part of the aggregate while new visits arrive.",
      "We therefore separate three statements that are often collapsed into one:",
      "- The code and delivery changes shipped successfully.",
      "- Lab and first-party measurements can show whether the targeted causes improved.",
      "- Search Console can later confirm whether the affected population moved out of the poor group.",
      "At publication time, the first statement is confirmed and the third remains under validation. Calling the issue resolved before the field window supports it would turn a useful case study into a marketing claim.",
    ],
  },
  {
    id: "shopify-transfer",
    heading: "What transfers to a Shopify performance investigation",
    blocks: [
      "Shugert.com.mx runs on TanStack Start and Lovable rather than Shopify. The exact cache middleware and rendering implementation do not copy into Liquid, Hydrogen or a Shopify app stack.",
      "The diagnostic sequence does transfer:",
      "- Start from field data and affected route groups.",
      "- Choose representative templates and customer states.",
      "- Identify the actual LCP element and critical request path.",
      "- Separate theme code, images, apps, consent tools and analytics.",
      "- Make bounded changes in staging.",
      "- Repeat comparable lab tests and wait for the field window before making population-level claims.",
      "For Shopify, the shared cause is often global theme JavaScript, app embeds, consent tooling, hero discovery or a script loaded on templates where it contributes nothing. The implementation differs; the evidence discipline should not.",
    ],
  },
  {
    id: "review-checklist",
    heading: "A practical review checklist for a multi-URL LCP regression",
    blocks: [
      "### Baseline",
      "- Export the affected URL group and record the date range.",
      "- Identify route or template families instead of treating every URL as unrelated.",
      "- Record cold-cache and warm-cache server response behavior.",
      "- Capture the LCP element, request chain, consent state and active third parties.",
      "### Change control",
      "- Tie each code change to a named suspected cause.",
      "- Keep redesign, dependency and unrelated content changes out of the same release.",
      "- Preserve a rollback path and a known production baseline.",
      "### Validation",
      "- Repeat the same route, viewport, connection and state.",
      "- Confirm the improvement did not create CLS, INP or functionality regressions.",
      "- Monitor route-level real-user data after deployment.",
      "- Wait for the field-data window before declaring the grouped issue resolved.",
    ],
  },
];

const FAQS = [
  {
    q: "Why can 36 different URLs have the same LCP issue?",
    a: "They may share the same layout, cache policy, hero-loading pattern, global scripts or third-party tooling. Search Console groups similar field behavior, so a shared failure often points to a shared delivery or template cause.",
  },
  {
    q: "Does a better Lighthouse score mean the Search Console issue is fixed?",
    a: "No. Lighthouse is a controlled lab run and Search Console reflects aggregated real-user field data over time. A lab improvement is useful diagnostic evidence, but the field group needs its own validation window.",
  },
  {
    q: "Should analytics and accessibility scripts be removed for performance?",
    a: "Not automatically. The first question is whether they can load after consent, load or idle time without losing their intended function. Removal should follow evidence and business approval, not a generic performance rule.",
  },
  {
    q: "Can the same method be used on Shopify?",
    a: "Yes. The method of grouping templates, identifying the LCP element, isolating global apps and scripts, staging bounded changes and validating equivalent states applies to Shopify. The code-level implementation depends on the theme, apps and storefront architecture.",
  },
];

const RELATED = [
  { label: "Service: Shopify Performance & Core Web Vitals", href: "/shopify-performance" },
  { label: "Pillar: Shopify Performance Optimization Guide", href: "/resources/shopify-performance-optimization-guide" },
  { label: "Shopify app bloat: audit global scripts before removing apps", href: "/resources/shopify-app-bloat-performance" },
  { label: "Shopify LCP problems in production themes", href: "/blog/shopify-lcp-problems-production-themes" },
  { label: "All performance resources", href: "/resources/performance" },
];

export const Route = createFileRoute("/resources/sitewide-lcp-regression-36-urls")({
  loader: async () => { await setEdgeHtmlCache(); },
  head: () =>
    buildHead({
      locale: "en",
      title: TITLE,
      description: DESC,
      path: PATH,
      altPath: ALT,
      ogImage: cover,
      type: "article",
      author: "Samuel Noriega",
      articleAuthor: "Samuel Noriega",
      articleSection: "Performance",
      publishedTime: PUBLISHED,
      modifiedTime: MODIFIED,
      jsonLd: [
        articleJsonLdForPillar({ image: cover,
          title: TITLE,
          description: DESC,
          path: PATH,
          publishedTime: PUBLISHED,
          modifiedTime: MODIFIED,
          section: "Performance",
        }),
        breadcrumbJsonLd([
          { name: "Home", path: "/" },
          { name: "Resources", path: "/resources" },
          { name: "Performance", path: "/resources/performance" },
          { name: "Site-wide LCP regression", path: PATH },
        ]),
        faqJsonLd(FAQS),
      ],
    }),
  component: () => (
    <SiteShell>
      <PillarArticle
        locale="en"
        path={PATH}
        eyebrow="Performance field note"
        guideLabel="Field note"
        title="How we diagnosed a site-wide LCP regression across 36 URLs"
        intro="Search Console reported one poor desktop LCP group across 36 commercial URLs. This is the exact reasoning path we used to move from an aggregated field signal to bounded changes, and why we are not calling it resolved before the field window confirms it."
        updated="July 2026"
        readingMinutes={12}
        hubHref="/resources/performance"
        hubLabel="Browse all performance resources"
        sections={SECTIONS}
        cover={{ src: cover, srcSet: COVER_SRCSET, sizes: COVER_SIZES, alt: "Performance field note illustrating a site-wide LCP investigation" }}
        midCTA={{
          eyebrow: "Use the same evidence discipline",
          title: "Get a performance scope tied to affected templates and comparable tests",
          body: "Send the store URL, the templates that matter and any Search Console or PageSpeed evidence you already have. We will define the baseline and validation method before recommending code changes.",
          href: "/get-a-quote?source=field-note-lcp-36-urls",
          label: "Request a performance scope",
        }}
        finalCTA={{
          eyebrow: "Shopify performance",
          title: "Fix the shared cause, not 36 URLs one at a time",
          body: "A senior engineer maps the field issue to the theme, apps, assets and delivery path, then returns a bounded implementation plan with staging and before/after validation.",
          href: "/shopify-performance",
          label: "See the performance service",
        }}
        faqs={FAQS}
        related={RELATED}
        publishedTime={PUBLISHED}
        modifiedTime={MODIFIED}
      />
    </SiteShell>
  ),
});
